import Dialog from "@mui/material/Dialog";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { CrudDialogHeader } from "./CrudDialogHeader";
import ListItemButton from "@mui/material/ListItemButton";
import type { Breakpoint } from "@mui/material/styles";
import {
  DialogContent,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const StyledToolbar = styled("div")(({ theme }) => theme.mixins.toolbar);

export interface CrudDialogStep {
  selected: boolean;
  onClick?: () => void;
  label: string;
}

export interface CrudDialogProps {
  steps: CrudDialogStep[];
  children?: React.ReactNode;
  open: boolean;
  onClose: () => void;
  id?: string;
  title?: string;
  subTitle?: string;
  fullScreen?: boolean;
  maxWidth?: Breakpoint | false;
}

export function CrudDialog({
  steps,
  children,
  open,
  onClose,
  title,
  subTitle,
  fullScreen = true,
  maxWidth = false,
}: CrudDialogProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const hasSteps = steps.length > 1;
  const activeStepIndex = steps.findIndex((step) => step.selected);

  const onChangeTab = (_event: React.SyntheticEvent, value: number) => {
    steps[value]?.onClick?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth={fullScreen}
      maxWidth={fullScreen ? false : maxWidth}
      PaperProps={{
        sx: {
          overflowY: "hidden",
          bgcolor: "background.default",
          ...(fullScreen ? {} : { height: "90vh" }),
        },
        elevation: 0,
      }}
    >
      <CrudDialogHeader
        position="sticky"
        onClose={onClose}
        secondary={title}
        primary={subTitle}
      />
      {!isDesktop && hasSteps && (
        <Tabs
          value={activeStepIndex === -1 ? false : activeStepIndex}
          onChange={onChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            backgroundColor: "background.paper",
            zIndex: (theme) => theme.zIndex.appBar,
            "& .MuiTab-root": {
              px: 1,
              minWidth: "auto",
            },
            "& .MuiTabScrollButton-root": {
              width: 32,
              px: 0,
            },
          }}
        >
          {steps.map((step, index) => (
            <Tab key={index} label={step.label} />
          ))}
        </Tabs>
      )}
      {isDesktop && hasSteps && (
        <Drawer
          variant="permanent"
          sx={{ zIndex: 0 }}
          PaperProps={{
            sx: {
              width: 300,
              borderColor: "background.default",
              backgroundColor: "background.default",
              // pt: 1,
            },
          }}
        >
          <StyledToolbar />
          <List
            sx={{
              backgroundColor: "background.paper",
              pt: 0,
              minHeight: "100%",
            }}
          >
            {steps.map((step, index) => (
              <ListItemButton
                key={index}
                selected={step.selected}
                component="a"
                href={`#tab-${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  step.onClick?.();
                }}
              >
                <ListItemText primary={step.label} />
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      )}
      <DialogContent
        sx={{
          marginLeft: isDesktop && hasSteps ? "300px" : 0,
          overflowY: "auto",
          p: isDesktop ? 1 : 0.5,
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}
